/**
 * The two-line conversation list item. INBOX scope item 8.
 *
 * ┌─ ⛔ NO EMPTY STATE THAT IS INDISTINGUISHABLE FROM A REAL ONE ───────────────┐
 * │ The rule that dropped the booking line, applied to every field here. It is  │
 * │ **not** "never show a placeholder" - it is that a placeholder must not be   │
 * │ confusable with a real value. Those give opposite answers per field, and    │
 * │ each one is decided below rather than defaulted.                            │
 * │                                                                            │
 * │   preview, no messages   OMITTED. An empty preview line is indistinguishable │
 * │                          from a message whose body is empty, and a          │
 * │                          conversation with no messages is a REAL state -    │
 * │                          25 exist in the seed by design. The item renders   │
 * │                          as ONE line, which is visibly different.           │
 * │   timestamp, no messages OMITTED, same argument. "-" or "never" would sit   │
 * │                          where a real time sits.                            │
 * │   preview, media message A TYPE LABEL ("Photo", "Voice note"). The body is  │
 * │                          legitimately null and the message is real, so      │
 * │                          omitting would hide it. "Photo" cannot be mistaken │
 * │                          for a text message that says Photo, because it is  │
 * │                          rendered as a label rather than as body text.      │
 * │   name, no display_name  FALLS BACK to the phone number, then to a guest    │
 * │                          label. ⚠️ THE OPPOSITE ANSWER, AND THE BUILD PLAN  │
 * │                          SAYS SO: "an empty name in the list is worse than  │
 * │                          a placeholder", because Meta does not always send  │
 * │                          a profile name immediately. A nameless row is      │
 * │                          unclickable and unreadable; a labelled one is not. │
 * │   avatar                 INITIALS for everyone. `contacts` has no photo     │
 * │                          column at all, so this is the designed fallback    │
 * │                          rather than a placeholder for something missing.   │
 * │                          Registered as `contact-avatar-source`.             │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ── ⛔ THE TIMESTAMP IS RELATIVE ONLY, AND THAT IS A CONSTRAINT NOT A CHOICE ─
 *
 * `ops/check-timezone-resolution.sh` forbids every timezone-aware formatting API
 * anywhere outside `src/time/display-timezone.ts`. Any absolute date needs a
 * display timezone, and resolving one inline is exactly the inlined fallback
 * `schema.md` forbids.
 *
 * ⚠️ THIS SAID *"which does not exist yet"* UNTIL 2026-09-12 AND THE MODULE HAD
 * EXISTED FOR TWO DAYS - `display-timezone-module` was closed on 2026-09-10 as a
 * settled-and-unclosed entry, and this comment kept its premise. **The reason
 * the row still shows a delta is now a different one and it is worth stating**:
 * an absolute date would have to be rendered by the SERVER, because that is
 * where the module lives, so it is a payload change on the list query rather
 * than a blocked build. The delta is also the right rendering for a list ordered
 * by recency, which is why nothing has asked for the change.
 *
 * ⚠️ THIS COMMENT DOES NOT NAME THOSE APIS, AND THE FIRST VERSION DID. The check
 * failed on its own documentation, because a grep cannot tell a comment
 * describing a rule from code breaking it. Exempting comments would let a real
 * call hide in one, so the check is right and the comment describes instead of
 * quoting. Second instance in this change; see `inbox.css`.
 *
 * So this renders a DELTA, which needs no timezone at all. ⚠️ It degrades for
 * old conversations ("63w"), and that is acceptable here because the default
 * list is active conversations ordered by recency. **An absolute date needs the
 * sanctioned module built first**, registered as `display-timezone-module` in
 * `ops/deferrals.txt`.
 */
import type { ConversationListRow } from '../../../src/services/conversation-list.js'
import { ChannelIcon } from './ChannelIcon.js'

/** Media types whose preview is a label rather than body text. */
const TYPE_LABEL: Record<string, string> = {
  image: 'Photo',
  video: 'Video',
  audio: 'Voice note',
  document: 'Document',
  sticker: 'Sticker',
  location: 'Location',
  contacts: 'Contact card',
  unsupported: 'Unsupported message',
}

/**
 * What the row is called.
 *
 * ⚠️ EXPORTED FOR THE TEST, because the fallback ORDER is the property worth
 * asserting and it is invisible from the rendered output when the first branch
 * happens to be populated.
 */
export function displayLabel(row: Pick<ConversationListRow, 'display_name' | 'phone_number'>): string {
  const name = row.display_name?.trim()
  if (name) return name
  const phone = row.phone_number?.trim()
  if (phone) return phone
  // The build plan's "DEFAULT GUEST NAME ... REPLACED once the real name is
  // known". A row with neither is a contact Meta gave us nothing for.
  return 'Guest'
}

/** Up to two initials. Falls back to the first character of whatever is shown. */
export function initialsFor(label: string): string | null {
  /**
   * ┌─ ⛔ LETTERS ONLY, AND `null` WHEN THERE ARE NONE. Amir's inspection: ─────┐
   * │ *"The grey circle is not recognisable as an avatar placeholder."*         │
   * │                                                                          │
   * │ ⚠️ THE CAUSE IS THE UNNAMED CONTACT. A contact with no display name falls │
   * │ back to their phone number, and `label.slice(0, 2)` turned                │
   * │ `+34600111222` into **"+3"** - which is not initials, not a placeholder,  │
   * │ and reads as a rendering fault. `null` lets the component draw a glyph    │
   * │ that says "person" instead.                                              │
   * └──────────────────────────────────────────────────────────────────────────┘
   */
  const words = label.split(/\s+/).filter((w) => /\p{L}/u.test(w))
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase()
  if (words.length === 1) {
    const letters = [...words[0]!].filter((c) => /\p{L}/u.test(c))
    if (letters.length > 0) return letters.slice(0, 2).join('').toUpperCase()
  }
  return null
}

/**
 * The channel's name, for the badge that was carrying colour alone.
 *
 * ⛔ AMIR'S INSPECTION: *"The channel indicator is colour-only - red Instagram,
 * green WhatsApp, no label, no tooltip. That fails for a colour-blind agent."*
 *
 * ⚠️ THE VALUE IS RETURNED VERBATIM WHEN UNKNOWN rather than mapped to
 * "Unknown". A channel this build has not heard of is still a real channel, and
 * its own name is more useful to whoever is looking at it than a placeholder.
 */
export function channelName(channel: string): string {
  const KNOWN: Record<string, string> = {
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    messenger: 'Messenger',
    email: 'Email',
  }
  return KNOWN[channel] ?? channel
}

/**
 * A delta, never an absolute date. See the timezone note above.
 *
 * ⚠️ `now` IS A PARAMETER. A function reading the clock cannot be tested for its
 * boundaries without freezing time globally, and the boundaries are the whole
 * of what is worth testing here.
 */
export function relativeTime(iso: string, now: Date): string {
  const seconds = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 1000))
  if (seconds < 60) return 'now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  return `${Math.floor(days / 7)}w`
}

/**
 * The preview line, or `null` when there is nothing real to show.
 *
 * ⛔ RETURNS null RATHER THAN AN EMPTY STRING, so the caller omits the element
 * instead of rendering an empty one. An empty `<p>` occupies the line and looks
 * like a message with no text.
 */
/**
 * ⛔ WHO WROTE IT, PREFIXED TO THE PREVIEW. Amir's inspection: the list showed
 * *"note here about stuff"* where it meant *"Dev Admin: note here about stuff"*,
 * so an internal note read exactly like something the customer said.
 *
 * ⚠️ THE CUSTOMER IS NOT PREFIXED, AND THAT IS THE POINT RATHER THAN AN
 * OMISSION. Their name is already the row's title two lines up, so "Marta
 * Reyes: hello" under a heading reading "Marta Reyes" spends the width that
 * makes the preview useful on repeating what is directly above it. **The prefix
 * exists to mark the lines that are NOT from the person named on the row.**
 *
 * ⚠️ AND `bot` IS LEFT UNPREFIXED TOO, deliberately: an automated reply is
 * outbound and reads as the business speaking, which is what it is. Finding 12
 * - naming WHICH automation - is a separate question that needs a producer this
 * codebase does not yet have.
 */
function authorPrefix(
  row: Pick<ConversationListRow, 'preview_sender_type' | 'preview_sender_name'>,
): string {
  if (row.preview_sender_type === 'system') return 'System: '
  if (row.preview_sender_type !== 'agent') return ''
  // ⚠️ A NAMED AGENT OR THE WORD, never a bare colon. `sender_name` is null if
  // the user row was removed, and "**: text**" reads as a rendering fault.
  const name = row.preview_sender_name?.trim()
  return name ? `${name}: ` : 'Agent: '
}

export function previewFor(
  row: Pick<
    ConversationListRow,
    'preview_body' | 'preview_type' | 'preview_at' | 'preview_sender_type' | 'preview_sender_name'
  >,
): { text: string; isLabel: boolean } | null {
  /**
   * ⚠️ MUTATION-TESTED AND FOUND EQUIVALENT, WHICH IS RECORDED RATHER THAN
   * TREATED AS A GAP. Removing this line does not fail any test, because when
   * `preview_at` is null the LEFT JOIN made every lateral column null too, so
   * `TYPE_LABEL['']` is undefined and the final `return null` produces the same
   * answer.
   *
   * It stays because the two returns state DIFFERENT REASONS - "there is no
   * message" and "there is a message with nothing to show" - and the equivalence
   * is a property of the data rather than a redundancy in the code. A later
   * caller with a different shape would find the guard doing real work.
   */
  if (row.preview_at === null) return null
  const who = authorPrefix(row)
  const body = row.preview_body?.trim()
  if (body) return { text: `${who}${body}`, isLabel: false }
  const label = TYPE_LABEL[row.preview_type ?? '']
  // ⚠️ THE PREFIX APPLIES TO THE MEDIA LABEL TOO. "Dev Admin: Photo" is the
  // same fact as "Dev Admin: some words" and the same question is being asked.
  if (label) return { text: `${who}${label}`, isLabel: true }
  // A text message with an empty body. Real, and there is nothing to show for
  // it, so the line is omitted rather than rendered blank.
  return null
}

/**
 * ⛔ `selected` WAS ABSENT UNTIL 2026-08-11, AND ITS ABSENCE WAS THE WHOLE GAP.
 *
 * This component never learned which conversation was open, so **clicking a row
 * changed the thread and left the list saying nothing about which one you were
 * reading.** No class, no rule, no `aria-current`. Found by the row-kind sweep,
 * recorded as `no-selected-state-in-the-conversation-list`, and built the same
 * day once Amir read the cost - it was four small pieces, and deferring it would
 * have reopened the same work later with the file closed.
 *
 * ┌─ ⛔ TWO SIGNALS ON ONE ROW, AND A ROW CAN CARRY BOTH ──────────────────────┐
 * │ Unread already owns WEIGHT (`font-weight: 700` on the name). Selected takes │
 * │ a LEFT BAR plus a RAISED SURFACE - a different axis entirely, so neither    │
 * │ competes with nor masks the other, and a row that is both unread AND open   │
 * │ shows both. That last case is the one that stops them collapsing into a     │
 * │ single "highlighted" state, and it is asserted in `row-kinds.spec.ts`.      │
 * │                                                                          │
 * │ ⚠️ NOT COLOUR ALONE, for the reason the notepad is not colour alone: the    │
 * │ bar is a geometric change as well as a coloured one, and the surface shift  │
 * │ survives a reader who cannot separate the accent from the border token.     │
 * │                                                                          │
 * │ ⛔ AND HOVER MOVES THE OPPOSITE WAY. Hover is `--color-surface-sunken`,     │
 * │ selected is `--color-surface-raised` - opposite directions from the base -  │
 * │ so hovering the open row does not read as deselecting it, which is what a   │
 * │ same-direction hover would look like.                                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ IT WAS INVISIBLE IN EVERY SCREENSHOT TAKEN THAT WEEK, including several
 * looked at deliberately while hunting layout defects. **Enumerating what should
 * exist found it; reading what does never would have.**
 */
export type ConversationListItemProps = {
  row: ConversationListRow
  now: Date
  /** Whether this is the conversation currently open in the thread pane. */
  selected?: boolean
  onOpen?: (id: string) => void
}

export function ConversationListItem({
  row,
  now,
  selected = false,
  onOpen,
}: ConversationListItemProps): React.JSX.Element {
  const label = displayLabel(row)
  const preview = previewFor(row)

  return (
    <li
      className={`conv-item${row.unread ? ' conv-item--unread' : ''}${
        selected ? ' conv-item--selected' : ''
      }`}
      data-testid="conversation-item"
      /**
       * ⚠️ WHICH CONVERSATION THIS ROW IS, READABLE FROM THE DOM. Added
       * 2026-09-11 for the contact-URL test, and it earns its place beyond that
       * test: without it, "the open thread is the right one" is only assertable
       * as "some row carries the selected class", which is the
       * presence-for-consequence substitution this project has an incident
       * about. The id is already in the URL; it is not a disclosure.
       */
      data-conversation-id={row.id}
    >
      <button
        type="button"
        className="conv-item__button"
        /**
         * ⛔ THE NON-VISUAL CARRIER, AND IT IS `aria-current` RATHER THAN
         * `aria-selected`. `aria-selected` belongs to a listbox or a grid, where
         * selection is a value the widget holds; this is a set of links into a
         * region, and `aria-current="true"` is the one that means "this is the
         * one you are on" without claiming a widget role the markup does not
         * have.
         *
         * ⚠️ IT IS OMITTED RATHER THAN SET FALSE. `aria-current="false"` is
         * announced by some screen readers as a state, so every unselected row
         * would say something about not being current - forty rows of noise for
         * one row's worth of signal.
         */
        aria-current={selected ? 'true' : undefined}
        onClick={() => onOpen?.(row.id)}
      >
        <span className="conv-item__avatar">
          {/*
            ⛔ INITIALS, OR A GLYPH THAT READS AS "PERSON". Amir's inspection:
            the grey circle was not recognisable as a placeholder. An unnamed
            contact falls back to their phone number, and the old code sliced
            that to "+3" - which looks like a bug rather than an absence.

            ⚠️ `aria-hidden` ON THE INITIALS ONLY, not on the whole avatar as it
            used to be. The badge inside now carries the channel's NAME for a
            screen reader, and hiding the container would hide that too - which
            is the accessible half of the very finding being fixed.
          */}
          <span aria-hidden="true" className="conv-item__initials">
            {initialsFor(label) ?? '●'}
          </span>
          {/*
            ⛔ THE CHANNEL BADGE CARRIED COLOUR AND NOTHING ELSE. Amir: *"red
            Instagram, green WhatsApp, no label, no tooltip. That fails for a
            colour-blind agent."*

            ⛔ AND ON 2026-09-12 THE DOT BECAME AN ICON, which is his finding 5
            and is a better fix than the one it replaces: a per-channel SHAPE was
            a second carrier BOLTED ON, and an icon differs by its geometry
            before anything is added to it. See `ChannelIcon`.

            ⚠️ THE OLD CARRIERS ARE KEPT RATHER THAN REPLACED - colour, `title`,
            and the name as text for a screen reader. A 14px glyph is legible to
            somebody who knows what they are looking for and not to somebody
            meeting it for the first time, and dropping a working carrier to add
            a better one is a net loss to whoever relied on the old one.
          */}
          <span
            className={`conv-item__badge conv-item__badge--${row.channel}`}
            title={channelName(row.channel)}
            data-testid="conv-item-channel"
          >
            <ChannelIcon channel={row.channel} />
            <span className="visually-hidden">{channelName(row.channel)}</span>
          </span>
        </span>

        <span className="conv-item__body">
          <span className="conv-item__line conv-item__line--top">
            {/*
              ┌─ ⛔ UNREAD'S SECOND CARRIER, ADDED 2026-08-20 ──────────────────┐
              │ Amir's inspection: *"No read/unread distinction anywhere in the │
              │ list. Nothing marks it."* And `row-kinds.spec.ts` test 1 was    │
              │ GREEN throughout, asserting an unread name is heavier than a    │
              │ read one.                                                       │
              │                                                                 │
              │ ⚠️ BOTH ARE TRUE. The only carrier was `font-weight`: 600 read  │
              │ against 700 unread - a difference the assertion can measure and │
              │ a person cannot see. **The test asked whether the weight is     │
              │ greater, not whether the row reads as unread.**                  │
              │                                                                 │
              │ ⛔ SO THIS IS THE TWO-CARRIER RULE, which the note highlight and │
              │ the arrived-at bubble already follow. A signal an agent scans    │
              │ hundreds of times a day cannot rest on one property, least of    │
              │ all the one that degrades most gently.                          │
              └─────────────────────────────────────────────────────────────────┘
            */}
            {row.unread && (
              <span className="conv-item__unread-dot" data-testid="conv-item-unread-dot">
                {/*
                  ⚠️ THE WORD, NOT ONLY THE SHAPE. A dot with `aria-hidden` makes
                  unread visible to everyone except the agent who most needs the
                  row's state announced rather than seen.
                */}
                <span className="visually-hidden">Unread</span>
              </span>
            )}
            <span className="conv-item__name">{label}</span>
            {/*
              ⛔ OMITTED ENTIRELY when there are no messages, rather than shown
              as "-". A dash sits where a real time sits.
            */}
            {row.last_message_at !== null && (
              <time className="conv-item__time" dateTime={row.last_message_at}>
                {relativeTime(row.last_message_at, now)}
              </time>
            )}
          </span>

          {/* ⛔ The element does not exist when there is nothing to preview. */}
          {preview !== null && (
            <span
              className={`conv-item__line conv-item__preview${
                preview.isLabel ? ' conv-item__preview--label' : ''
              }`}
            >
              {preview.text}
            </span>
          )}
        </span>
      </button>
    </li>
  )
}
